<script setup lang="ts">
import { computed, onMounted, useTemplateRef, watch } from "vue"
import { useFormField } from "../composables/useFormField"

const props = defineProps<{
  name: string
  label: string
  required?: boolean
  help?: string
  validate?: (value: unknown) => string | null
  getValue?: () => unknown
  /**
   * Override which top-level form-data key drives the dirty indicator.
   * Defaults to `name`. Set this when a single top-level field is edited
   * through multiple FormFields — e.g. a rating object edited one criterion
   * per FormField, all pointing at `rating` so the dot lights up whenever
   * any criterion changes.
   */
  dirtyField?: string
}>()

const field = useFormField({
  name: props.name,
  required: props.required,
  validate: props.validate,
  getValue: props.getValue,
  dirtyField: props.dirtyField,
  label: props.label
})

const showError = computed(() => field.touched.value && field.error.value !== null)

const labelId = computed(() => `${field.id}-label`)
const errorId = computed(() => `${field.id}-error`)

const rootRef = useTemplateRef<HTMLElement>("root")

// The control lives in a <slot>, so wire the accessible name and error state
// onto the rendered native control(s) directly instead of through props. This
// gives every FormField input a programmatic label (and, when invalid, an
// error association) without touching each call site.
function applyAria(): void {
  const root = rootRef.value
  if (!root) return
  const controls = root.querySelectorAll<HTMLElement>("input, select, textarea")
  const invalid = showError.value
  for (const el of controls) {
    el.setAttribute("aria-labelledby", labelId.value)
    if (invalid) {
      el.setAttribute("aria-invalid", "true")
      el.setAttribute("aria-describedby", errorId.value)
    } else {
      el.removeAttribute("aria-invalid")
      el.removeAttribute("aria-describedby")
    }
  }
}

onMounted(applyAria)
watch(showError, applyAria)

function handleBlur() {
  field.setTouched(true)
}
</script>

<template>
  <fieldset ref="root" class="fieldset min-w-0 w-full [min-inline-size:0]" @focusout="handleBlur">
    <legend class="fieldset-legend text-primary uppercase text-xs tracking-wider font-semibold flex items-center gap-2 w-full">
      <span class="flex-1 min-w-0">
        <span
          v-if="field.dirty.value"
          data-testid="field-dirty-indicator"
          class="inline-block w-2 h-2 rounded-full bg-warning align-middle mr-1.5"
          aria-label="Ungespeicherte Änderung"
        /><span :id="labelId">{{ label }}</span><span v-if="required" class="text-error ml-0.5">*</span>
      </span>
      <span v-if="$slots['label-trailing']" class="shrink-0 normal-case tracking-normal font-normal">
        <slot name="label-trailing" />
      </span>
    </legend>

    <slot />

    <div
      v-if="!showError && ($slots.help || help)"
      class="label text-base-content/60 text-xs leading-snug whitespace-normal block"
    >
      <slot name="help">{{ help }}</slot>
    </div>

    <div
      v-if="showError"
      :id="errorId"
      role="alert"
      class="label text-error text-xs leading-snug whitespace-normal block"
    >
      {{ field.error.value }}
    </div>
  </fieldset>
</template>
